/**
 * The Beer–Lambert law, A = ε·l·c, and the absorbance ↔ transmittance relation
 * A = −log₁₀ T = 2 − log₁₀(%T).
 *
 * IUPAC's name for ε is the molar (decadic) absorption coefficient, with unit
 * dm³·mol⁻¹·cm⁻¹ (= L·mol⁻¹·cm⁻¹ = M⁻¹·cm⁻¹); its SI unit is m²·mol⁻¹, and
 * 1 m²/mol = 10 M⁻¹cm⁻¹. Working units are M⁻¹cm⁻¹, centimetres and mol/L.
 */

import { MOLARITY, type UnitDef } from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { type HeatFailure, type ReadSpec, readAll, unitFactor } from "./heat";

export type BeerFailure =
  | HeatFailure
  | {
      ok: false;
      reason: "negativeAbsorbance" | "transmittanceRange" | "zeroAbsorbance";
    };

export const ABSORPTION_COEFFICIENT_UNITS: readonly UnitDef[] = [
  { id: "M", symbol: "M⁻¹ cm⁻¹", factor: 1 },
  { id: "mM", symbol: "mM⁻¹ cm⁻¹", factor: 1000 },
  { id: "SI", symbol: "m² mol⁻¹", factor: 10 },
];

export const PATH_LENGTH_UNITS: readonly UnitDef[] = [
  { id: "cm", symbol: "cm", factor: 1 },
  { id: "mm", symbol: "mm", factor: 0.1 },
  { id: "m", symbol: "m", factor: 100 },
];

export const CONCENTRATION_UNITS: readonly UnitDef[] = MOLARITY.units
  .filter((u) => ["nM", "uM", "mM", "M"].includes(u.id))
  .map((u) => ({
    id: u.id,
    symbol: u.id === "uM" ? "µM" : u.id,
    factor: u.factor,
  }));

export type SignalUnit = "A" | "pctT" | "T";
export const SIGNAL_UNITS: readonly { id: SignalUnit; symbol: string }[] = [
  { id: "A", symbol: "A" },
  { id: "pctT", symbol: "%T" },
  { id: "T", symbol: "T" },
];

/* ------------------------------------------- absorbance ↔ transmittance */

export function absorbanceFromTransmittance(fraction: number): number {
  return -Math.log10(fraction);
}

export function transmittanceFromAbsorbance(absorbance: number): number {
  return 10 ** -absorbance;
}

export interface Signal {
  /** Absorbance, unitless. */
  a: number;
  /** Transmittance as a fraction, 0 < T ≤ 1. */
  t: number;
  /** Transmittance in percent. */
  pctT: number;
  /** Percent of the incident light absorbed. */
  absorbedPct: number;
}

export function signalFromAbsorbance(a: number): Signal {
  const t = transmittanceFromAbsorbance(a);
  return { a, t, pctT: t * 100, absorbedPct: (1 - t) * 100 };
}

/** Reads an absorbance or a transmittance entry and returns the full signal. */
export function readSignal(
  text: string,
  unit: string
): { signal: Signal } | BeerFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", key: "a" };
  }
  if (unit === "A") {
    return typed < 0
      ? { ok: false, reason: "negativeAbsorbance" }
      : { signal: signalFromAbsorbance(typed) };
  }
  const fraction = unit === "pctT" ? typed / 100 : typed;
  return fraction > 0 && fraction <= 1
    ? { signal: signalFromAbsorbance(absorbanceFromTransmittance(fraction)) }
    : { ok: false, reason: "transmittanceRange" };
}

export function convertSignal(
  text: string,
  unit: string
): { signal: Signal } | BeerFailure {
  return readSignal(text, unit);
}

/* ------------------------------------------------------- A = ε l c */

export type BeerTerm = "a" | "e" | "l" | "c";
export const BEER_TERMS: readonly BeerTerm[] = ["a", "e", "l", "c"];

export interface BeerInput {
  unknown: BeerTerm;
  text: Record<BeerTerm, string>;
  /** Unit ids: signal (A, %T or T), ε, path length, concentration. */
  unit: { a: string; e: string; l: string; c: string };
}

export interface BeerSolution {
  ok: true;
  unknown: BeerTerm;
  signal: Signal;
  /** M⁻¹cm⁻¹, cm, mol/L */
  e: number;
  l: number;
  c: number;
  answer: number;
  answerUnit: string;
}

export type BeerOutcome = BeerSolution | BeerFailure;

const BEER_UNIT_LISTS = {
  e: ABSORPTION_COEFFICIENT_UNITS,
  l: PATH_LENGTH_UNITS,
  c: CONCENTRATION_UNITS,
} as const;

export function solveBeer(input: BeerInput): BeerOutcome {
  const { unknown, unit } = input;
  let signal: Signal | null = null;
  if (unknown !== "a") {
    const read = readSignal(input.text.a, unit.a);
    if ("ok" in read) {
      return read;
    }
    if (read.signal.a === 0) {
      return { ok: false, reason: "zeroAbsorbance" };
    }
    signal = read.signal;
  }
  const specs: ReadSpec[] = (["e", "l", "c"] as const)
    .filter((term) => term !== unknown)
    .map((term) => ({
      key: term,
      text: input.text[term],
      kind: "positive" as const,
      unit: "",
      factor: unitFactor(BEER_UNIT_LISTS[term], unit[term]),
    }));
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  const a = signal?.a ?? v.e * v.l * v.c;
  const values = {
    e: unknown === "e" ? a / (v.l * v.c) : v.e,
    l: unknown === "l" ? a / (v.e * v.c) : v.l,
    c: unknown === "c" ? a / (v.e * v.l) : v.c,
  };
  const full = signal ?? signalFromAbsorbance(a);
  let answer: number;
  if (unknown === "a") {
    answer = convertFromSignal(full, unit.a);
  } else {
    answer =
      values[unknown] / unitFactor(BEER_UNIT_LISTS[unknown], unit[unknown]);
  }
  return {
    ok: true,
    unknown,
    signal: full,
    ...values,
    answer,
    answerUnit: unit[unknown],
  };
}

export function convertFromSignal(signal: Signal, unit: string): number {
  if (unit === "pctT") {
    return signal.pctT;
  }
  return unit === "T" ? signal.t : signal.a;
}

/** Most spectrophotometers read best between A = 0.1 and A = 1.0 (about 80% to 10% T). */
export const GOOD_RANGE = { low: 0.1, high: 1 } as const;

export function rangeStatus(a: number): "low" | "good" | "high" {
  if (a < GOOD_RANGE.low) {
    return "low";
  }
  return a > GOOD_RANGE.high ? "high" : "good";
}
